
import { applicantStore } from '@/store'
import { ApplicationDocument } from '@/commons/types'
import { Component, Vue } from 'vue-property-decorator'
import { ApplicationUtils } from '@/commons/utils/application'
import { TranslationUtils } from '@/commons/utils/translations'
import { PlaceOfIssueUtils } from '@/commons/utils/place.of.issue'
import {
    APPLICANT_MIN_AGE,
    ApplicationDeliveryType,
    AUTHORIZED_PERSON_MIN_AGE
} from '@/commons/constants/application'
import { GeneralError } from '@/store/entities'

@Component
export default class PlaceOfIssueSummary extends Vue {
    get isExpeditedIdCard (): boolean {
        return applicantStore.idCard.expedited
    }

    get isExpeditedCitizenPassport (): boolean {
        return applicantStore.citizenPassport.expedited
    }

    get placeOfIssueCitizenPassport (): string {
        return applicantStore.citizenPassport.delivery?.location || ''
    }

    get placeOfIssueIdCard (): string {
        return applicantStore.idCard.delivery?.location || ''
    }

    applicationDocumentBasedOnType (documentType: string): ApplicationDocument {
        return ApplicationUtils.getDocument(documentType)
    }

    get issuedTo (): string {
        const issuedToApplicant = `${this.$t('summary.issuedTo.applicant')} ${applicantStore.applicantFullName} ${this.$t('summary.inPerson')}`
        if (applicantStore.getApplication.idCard?.isWithAuthorized || applicantStore.getApplication.citizenPassport?.isWithAuthorized) {
            return this.issuedToWhenAuthorizedPersonInvolved()
        }
        return applicantStore.applicantAge != null && applicantStore.applicantAge >= APPLICANT_MIN_AGE ? issuedToApplicant : `${this.$t('summary.issuedTo.representative')}`
    }

    summaryTextWhenBothDocumentsAuthorized (idCard: ApplicationDocument, citizenPassport: ApplicationDocument): string {
        if (idCard.authorizedPerson === null || citizenPassport.authorizedPerson === null) {
            throw new GeneralError(['errors.system.title'])
        }
        if (idCard.authorizedPerson.personCode === citizenPassport.authorizedPerson.personCode) {
            return this.$t('summary.issuedTo.multipleDocumentsAndSameAuthorizedPerson', {
                fullName: idCard.authorizedPerson.firstName + ' ' + idCard.authorizedPerson.lastName
            }).toString()
        } else {
            return this.$t('summary.issuedTo.multipleDocumentsAndAuthorizedPersons', {
                idCardFullName: idCard.authorizedPerson.firstName + ' ' + idCard.authorizedPerson.lastName,
                passportFullName: citizenPassport.authorizedPerson.firstName + ' ' + citizenPassport.authorizedPerson.lastName
            }).toString()
        }
    }

    summaryTextWhenOneDocumentAuthorized (idCardWithAuthorized: boolean, idCard: ApplicationDocument | null, citizenPassport: ApplicationDocument | null): string {
        if (applicantStore.applicantAge == null) {
            return ''
        }
        if (idCardWithAuthorized) {
            if (idCard === null || idCard.authorizedPerson === null) {
                throw new GeneralError(['errors.system.title'])
            }
            const idCardFullName = idCard.authorizedPerson.firstName + ' ' + idCard.authorizedPerson.lastName
            return this.summaryTextWhenApplicantOrRepresentativeInvolved(this.$t('summary.idCard').toString(), idCardFullName.toUpperCase(), this.$t('summary.citizenPassport').toString())
        } else {
            if (citizenPassport === null || citizenPassport.authorizedPerson === null) {
                throw new GeneralError(['errors.system.title'])
            }
            const passportFullName = citizenPassport.authorizedPerson.firstName + ' ' + citizenPassport.authorizedPerson.lastName
            return this.summaryTextWhenApplicantOrRepresentativeInvolved(this.$t('summary.citizenPassport').toString(), passportFullName.toUpperCase(), this.$t('summary.idCard').toString())
        }
    }

    summaryTextWhenApplicantOrRepresentativeInvolved (firstDocumentType: string, fullName: string, secondDocumentType: string): string {
        if (applicantStore.applicantAge === null) {
            throw new GeneralError(['errors.system.title'])
        }
        if (applicantStore?.applicantAge < AUTHORIZED_PERSON_MIN_AGE) {
            return this.summaryTextWhenRepresentativeAndAuthorizedPerson(firstDocumentType, fullName.toUpperCase(), secondDocumentType)
        } else {
            return this.summaryTextWhenApplicantAndAuthorizedPerson(firstDocumentType, fullName.toUpperCase(), secondDocumentType)
        }
    }

    summaryTextWhenApplicantAndAuthorizedPerson (documentAuthorized: string, fullName: string, document: string): string {
        return this.$t('summary.issuedTo.multipleDocumentsApplicantAndAuthorizedPerson', {
            authorizedDocument: this.capitalizeFirstLetter(documentAuthorized),
            authorizedName: fullName,
            otherDocument: document,
            applicantName: applicantStore.applicantFullName
        }) as string
    }

    summaryTextWhenRepresentativeAndAuthorizedPerson (documentAuthorized: string, fullName: string, document: string): string {
        return this.$t('summary.issuedTo.multipleDocumentsRepresentativeAndAuthorizedPerson', {
            authorizedDocument: this.capitalizeFirstLetter(documentAuthorized),
            authorizedName: fullName,
            otherDocument: document
        }) as string
    }

    summaryTextWhenIssuedToAuthorizedPerson (idCard: ApplicationDocument | null, passport: ApplicationDocument | null, isIdCard: boolean): string {
        if (isIdCard) {
            if (idCard == null || idCard.authorizedPerson == null) {
                throw new GeneralError()
            } else {
                return this.$t('summary.issuedTo.authorizedPerson', {
                    fullName: idCard.authorizedPerson.firstName + ' ' + idCard.authorizedPerson.lastName
                }) as string
            }
        } else {
            if (passport == null || passport.authorizedPerson == null) {
                throw new GeneralError()
            } else {
                return this.$t('summary.issuedTo.authorizedPerson', {
                    fullName: passport.authorizedPerson.firstName + ' ' + passport.authorizedPerson.lastName
                }) as string
            }
        }
    }

    issuedToWhenAuthorizedPersonInvolved (): string {
        const idCard = applicantStore.getApplication.idCard
        const citizenPassport = applicantStore.getApplication.citizenPassport
        const idCardWithAuthorized = !!idCard?.isWithAuthorized
        const passportWithAuthorized = !!citizenPassport?.isWithAuthorized

        if (idCard?.reason !== null && citizenPassport?.reason !== null) {
            if (idCardWithAuthorized && passportWithAuthorized) {
                return this.summaryTextWhenBothDocumentsAuthorized(idCard, citizenPassport)
            } else {
                return this.summaryTextWhenOneDocumentAuthorized(idCardWithAuthorized, idCard, citizenPassport)
            }
        } else {
            return this.summaryTextWhenIssuedToAuthorizedPerson(idCard, citizenPassport, idCard?.reason != null).toString()
        }
    }

    capitalizeFirstLetter (document: string): string {
        return document.charAt(0).toUpperCase() + document.slice(1)
    }

    placeOfIssueLabel (documentType: string): string {
        const placeOfIssue = PlaceOfIssueUtils.fetchPlaceOfIssue(
            this.applicationDocumentBasedOnType(documentType),
            documentType
        )
        return placeOfIssue ? TranslationUtils.translateClassifier(placeOfIssue) : ''
    }

    get isCourierRetrieval (): boolean {
        return applicantStore.citizenPassport?.delivery.type === ApplicationDeliveryType.COURIER_RETRIEVAL
    }

    get courierCity (): string | null | undefined {
        return applicantStore.citizenPassport?.delivery.courier?.city
    }

    get courierPostalCode (): string | null | undefined {
        return applicantStore.citizenPassport?.delivery.courier?.postalCode
    }
}
